import { useMutation } from "@apollo/client";
import { CANCEL_CHANGE_REQUEST_MUTATION, Node } from "../queries";
import Button from "@/components/Button";
import Progress from "@/components/Progress";
import TextField from "@/components/TextField";
import useModal from "@/hooks/useModal";
import { useForm } from "react-hook-form";

type FormData = { message: string };

export default function CancelModal({
  changeRequest: { id: changeRequestId },
  onClosed
}: {
  changeRequest: Node;
  onClosed(): void;
}) {
  const { save, Modal, close } = useModal();
  const {
    handleSubmit,
    register,
    formState: { errors },
    watch
  } = useForm<FormData>({ defaultValues: { message: "" } });

  const [send, { loading }] = useMutation(CANCEL_CHANGE_REQUEST_MUTATION);

  const submit = ({ message }: FormData) =>
    save(
      () => send({ variables: { id: changeRequestId, message } }),
      (data) => data.cancelChangeRequest,
      "Change request canceled"
    );

  return (
    <Modal
      className="w-[600px] max-w-[95vw]"
      actions={
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
          <Button variant="text" onClick={close} disabled={loading}>
            Go Back
          </Button>
          <Button
            theme="error"
            disabled={loading}
            onClick={handleSubmit(submit)}
            icon="cancel"
            testId="confirm-cancel-change-request"
          >
            Cancel Change Request
          </Button>
        </div>
      }
      headline="Cancel Change Request"
      onClosed={onClosed}
    >
      {loading ? (
        <Progress variant="linear" />
      ) : (
        <div className="flex flex-col gap-6">
          <p>This cancels the change request. The member will not be emailed. Add an internal note explaining why.</p>

          <TextField
            className="w-full h-48"
            variant="outlined"
            type="textarea"
            label="Internal Note *"
            error={!!errors.message}
            errorText={errors.message?.message}
            value={watch("message")}
            {...register("message", { required: "Internal note is required" })}
          />
        </div>
      )}
    </Modal>
  );
}
